import { useState, useRef } from 'react'
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { nutritionAPI, profileAPI } from '../services/api'
import { toArrayOr } from '../utils/arrays'
import { CakeIcon, CameraIcon, XMarkIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import FoodPhotoUpload from '../components/nutrition/FoodPhotoUpload'
import FoodAnalysisReview from '../components/nutrition/FoodAnalysisReview'
import SavedMealEditCard from '../components/nutrition/SavedMealEditCard'
import ManualFoodTextEntry from '../components/nutrition/ManualFoodTextEntry'
import MealTargetSelector from '../components/nutrition/MealTargetSelector'
import TodaySummaryCard from '../components/nutrition/TodaySummaryCard'
import MealTimelineCard from '../components/nutrition/MealTimelineCard'
import MealPlaceholder from '../components/nutrition/MealPlaceholder'
import RecentDayCard from '../components/nutrition/RecentDayCard'
import WeeklySummaryWidget from '../components/nutrition/WeeklySummaryWidget'
import FavoriteMealsWidget from '../components/nutrition/FavoriteMealsWidget'
import FavoriteMealsModal from '../components/nutrition/FavoriteMealsModal'
import MealLogActions from '../components/nutrition/MealLogActions'
import { apiErrorMessage, isOutOfCredit } from '../utils/apiError'
import FavoritesQuickPickSheet from '../components/nutrition/FavoritesQuickPickSheet'
import WeekPlanCard from '../components/nutrition/WeekPlanCard'

export default function Nutrition() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [showPhotoUpload, setShowPhotoUpload] = useState(false)
  const [showManualTextEntry, setShowManualTextEntry] = useState(false)
  const [showFavoritesModal, setShowFavoritesModal] = useState(false)
  // VH-165: phone block. `photoSourceMode` tells FoodPhotoUpload to open the
  // camera (or library) straight away instead of showing a source chooser;
  // null keeps the existing desktop behaviour untouched.
  const [photoSourceMode, setPhotoSourceMode] = useState(null)
  const [showFavouritesSheet, setShowFavouritesSheet] = useState(false)
  const [analysisResult, setAnalysisResult] = useState(null)
  const [showMealTypeSelector, setShowMealTypeSelector] = useState(false)
  const [selectedMealType, setSelectedMealType] = useState('breakfast')
  const [selectedDate, setSelectedDate] = useState(() => {
    // Initialize with local date
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  })
  const [isSaving, setIsSaving] = useState(false)
  const uploadComponentRef = useRef(null)

  // Store batch files in parent component
  const [batchFiles, setBatchFiles] = useState([])
  const [currentBatchIndex, setCurrentBatchIndex] = useState(0)
  const [photoPreviews, setPhotoPreviews] = useState([])

  // Auto-save state
  const [savedMealData, setSavedMealData] = useState(null)
  const [autoSaveError, setAutoSaveError] = useState(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)

  // Meal target selection for photo save (VH-23)
  const [existingMealsForPhoto, setExistingMealsForPhoto] = useState([])
  const [selectedPhotoMealId, setSelectedPhotoMealId] = useState(null)

  // Dashboard state
  const [expandedMeals, setExpandedMeals] = useState({})
  const [expandedDays, setExpandedDays] = useState({})
  const [dayMeals, setDayMeals] = useState({}) // Cache meals by date

  // Fetch user's nutrition goals from profile
  const { data: currentGoal } = useQuery({
    queryKey: ['currentGoal'],
    queryFn: async () => {
      const response = await profileAPI.getCurrentGoal()
      return response.data
    },
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  })

  // Fetch health metrics (for TDEE fallback if no goal set)
  const { data: healthMetrics } = useQuery({
    queryKey: ['healthMetrics', 'moderate'],
    queryFn: async () => {
      const response = await profileAPI.getHealthMetrics('moderate')
      return response.data
    },
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  })

  // Calculate user goals from current goal or TDEE
  const userGoals = {
    calories: currentGoal?.target_calories || Math.round(healthMetrics?.tdee || 2000),
    protein: currentGoal?.target_protein_g || 50,
    carbs: currentGoal?.target_carbs_g || 250,
    fat: currentGoal?.target_fat_g || 75
  }

  // Helper function to get local date string (YYYY-MM-DD)
  const getLocalDateString = (date = new Date()) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Selected date for viewing meals (defaults to today in local timezone)
  const today = getLocalDateString()
  const [selectedViewDate, setSelectedViewDate] = useState(today)

  // VH-231: on training days a health-cycle goal has a higher carbohydrate target
  const { data: goalForDate } = useQuery({
    queryKey: ['currentGoal', selectedViewDate],
    queryFn: async () => (await profileAPI.getCurrentGoal(selectedViewDate)).data,
    staleTime: 5 * 60 * 1000
  })
  if (goalForDate?.carbs_for_date_g) {
    userGoals.carbs = goalForDate.carbs_for_date_g
  }

  const { data: foodLogs, isLoading, error: _foodLogsError } = useQuery({
    queryKey: ['foodLogs'],
    queryFn: async () => {
      const response = await nutritionAPI.getFoodLogs({ limit: 10 })
      return response.data
    },
    staleTime: 0, // Always refetch when invalidated
    gcTime: 0, // Don't cache
    retry: false // Don't retry on error so we can see it immediately
  })

  // Fetch meals for the selected date
  const { data: selectedDateMeals, isLoading: isMealsLoading } = useQuery({
    queryKey: ['meals', selectedViewDate],
    queryFn: async () => {
      const response = await nutritionAPI.getMealsByDate(selectedViewDate)
      return response.data
    },
    staleTime: 0, // Always refetch when invalidated
    gcTime: 0, // Don't cache
  })

  // Fetch weekly summary data
  const { data: weeklySummary } = useQuery({
    queryKey: ['weeklySummary'],
    queryFn: async () => {
      try {
        const response = await nutritionAPI.getCurrentWeekReport()
        return response.data
      } catch (error) {
        console.error('Error fetching weekly summary:', error)
        return null
      }
    },
    staleTime: 60000, // Cache for 1 minute
    retry: false
  })

  // Delete meal mutation
  const deleteMealMutation = useMutation({
    mutationFn: (mealId) => nutritionAPI.deleteMeal(mealId),
    onSuccess: () => {
      // `dayMeals` is a LOCAL cache of the meals inside each expanded Recent Day
      // and TanStack knows nothing about it, so invalidating queries alone left
      // a deleted meal on screen until the day was collapsed and reopened.
      // Every other writer clears it; delete was the one that did not.
      setDayMeals({})
      queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
      // Not scoped to selectedViewDate: a meal can be deleted from an expanded
      // Recent Day, which is by definition a DIFFERENT date, and that key would
      // never be invalidated.
      queryClient.invalidateQueries({ queryKey: ['meals'] })
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
      queryClient.invalidateQueries({ queryKey: ['mealCopySource'] })
    },
    onError: (error) => {
      console.error('Error deleting meal:', error)
      alert(t('nutrition.errors.deleteMealFailedWithError', { error: error.response?.data?.detail || error.message || t('nutrition.errors.unknownError') }))
    }
  })

  // === VH-141: "same as last time" ===============================================
  // Ask, per meal type, which earlier day could be copied. Only for types that
  // are actually EMPTY on the viewed day: the shortcut exists to fill an empty
  // slot cheaply, and offering it on a filled slot invites accidental
  // duplication. Past days are excluded — you do not retro-fill last Tuesday.
  const copyableTypes = ['BREAKFAST', 'LUNCH', 'DINNER', 'SNACK']
  const loggedTypesForDate = new Set(
    (selectedDateMeals?.meals || []).map((m) => (m.meal_type || '').toUpperCase())
  )
  const copySourceQueries = useQueries({
    queries: copyableTypes.map((type) => ({
      queryKey: ['mealCopySource', selectedViewDate, type],
      queryFn: async () => {
        const r = await nutritionAPI.getMealCopySource(type.toLowerCase(), selectedViewDate)
        return r.data
      },
      enabled: selectedViewDate >= today && !loggedTypesForDate.has(type),
      staleTime: 60_000,
    })),
  })
  const copySources = {}
  copyableTypes.forEach((type, i) => {
    copySources[type] = copySourceQueries[i]?.data || null
  })

  const [copyingMealType, setCopyingMealType] = useState(null)
  const copyMealMutation = useMutation({
    mutationFn: ({ mealType, sourceDate }) =>
      nutritionAPI.copyMeals(mealType.toLowerCase(), sourceDate, selectedViewDate),
    onSuccess: () => {
      setDayMeals({}) // Clear stale dayMeals cache, as the other writers do
      queryClient.invalidateQueries({ queryKey: ['meals', selectedViewDate] })
      queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
      queryClient.invalidateQueries({ queryKey: ['mealCopySource', selectedViewDate] })
    },
    onError: (error) => {
      console.error('Error copying meal:', error)
      // 409 is the server refusing a double tap — a specific, non-alarming case.
      alert(
        error.response?.status === 409
          ? t('nutrition.placeholder.copyAlreadyLogged')
          : t('nutrition.placeholder.copyFailed')
      )
    },
    onSettled: () => setCopyingMealType(null),
  })

  // VH-161: source-first copy — start from a meal already logged and send it to
  // a day of your choosing. VH-141's copy only appears on an EMPTY slot for the
  // selected day, so once a day is fully logged there is no copy affordance at
  // all. Returns the promise so the dialog can surface a 400/409 rather than
  // failing silently.
  const handleCopyMealToDate = ({ mealId, targetDate }) =>
    nutritionAPI.copyMeal(mealId, targetDate).then((res) => {
      setDayMeals({})
      queryClient.invalidateQueries({ queryKey: ['meals'] })
      queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
      queryClient.invalidateQueries({ queryKey: ['mealCopySource'] })
      return res
    })

  const handleCopyMeal = (mealType, sourceDate) => {
    if (copyMealMutation.isPending) return // guard the double tap client-side too
    setCopyingMealType(mealType)
    copyMealMutation.mutate({ mealType, sourceDate })
  }

  // Get food log data for the selected date
  const selectedDateLog = foodLogs?.food_logs?.find(log => {
    const logDate = log.log_date || log.date
    // Extract just the date part (YYYY-MM-DD) from datetime string
    const logDateOnly = logDate.split('T')[0]
    return logDateOnly === selectedViewDate
  }) || foodLogs?.data?.find(log => {
    const logDate = log.log_date || log.date
    const logDateOnly = logDate.split('T')[0]
    return logDateOnly === selectedViewDate
  })

  // Function to fetch meals for a specific day
  const fetchMealsForDay = async (date) => {
    if (dayMeals[date]) {
      // Already fetched
      return
    }

    try {
      const response = await nutritionAPI.getMealsByDate(date)
      setDayMeals(prev => ({
        ...prev,
        [date]: response.data.meals || []
      }))
    } catch (error) {
      console.error(`Error fetching meals for ${date}:`, error)
      setDayMeals(prev => ({
        ...prev,
        [date]: []
      }))
    }
  }

  // Handle day card toggle - fetch meals when expanding
  const handleDayToggle = (logId, date) => {
    const isExpanding = !expandedDays[logId]

    setExpandedDays(prev => ({
      ...prev,
      [logId]: isExpanding
    }))

    if (isExpanding) {
      fetchMealsForDay(date)
    }
  }

  // Called when FoodPhotoUpload starts analyzing a photo
  const handleAnalysisStart = (files, fileIndex) => {
    console.log(`[Nutrition] Analysis starting for photo ${fileIndex + 1}/${files.length}`)
    setBatchFiles(files)
    setCurrentBatchIndex(fileIndex)
    setIsAnalyzing(true)
    setAnalysisResult(null)
    setSavedMealData(null)

    // Preload all photo previews for instant display
    if (photoPreviews.length === 0 && files.length > 0) {
      const previewPromises = files.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.readAsDataURL(file)
        })
      })

      Promise.all(previewPromises).then(previews => {
        setPhotoPreviews(previews)
      })
    }
  }

  const handleAnalysisComplete = async (result, files = [], fileIndex = 0) => {
    // Analysis complete, now saving
    setIsAnalyzing(false)

    // Store batch info in parent state (in case not set by handleAnalysisStart)
    if (files.length > 0 && batchFiles.length === 0) {
      setBatchFiles(files)
      setCurrentBatchIndex(fileIndex)

      // Preload all photo previews for instant display
      const previewPromises = files.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.readAsDataURL(file)
        })
      })

      Promise.all(previewPromises).then(previews => {
        setPhotoPreviews(previews)
      })
    }

    // Auto-save using suggested meal type from EXIF/current time
    setIsSaving(true)
    setAutoSaveError(null)

    try {
      // Use suggested values from backend (EXIF or current time)
      const mealType = result.suggested_meal_type || 'snack'
      const logDate = result.suggested_date || getLocalDateString()
      const mealTime = result.exif_timestamp || new Date().toISOString()

      // VH-23: Check for existing meals of this type on this date
      // If found, pause auto-save and let user choose
      try {
        const mealsResponse = await nutritionAPI.getMealsByDate(logDate)
        const mealsOfType = (mealsResponse.data?.meals || []).filter(
          (m) => m.meal_type?.toLowerCase() === mealType.toLowerCase()
        )
        if (mealsOfType.length > 0) {
          console.log(`[Nutrition] Existing ${mealType} meals found for ${logDate}, showing choice UI`)
          setExistingMealsForPhoto(mealsOfType)
          setSelectedPhotoMealId(null)
          setAnalysisResult(result)
          setSelectedMealType(mealType)
          setSelectedDate(logDate)
          setShowMealTypeSelector(true)
          setIsSaving(false)
          return
        }
      } catch {
        // Non-critical — if fetch fails, just auto-save as new
      }

      console.log(`[Nutrition] Auto-saving meal: ${mealType} on ${logDate}`)

      const saveData = {
        analysis_data: result,
        meal_type: mealType,
        meal_time: mealTime,
        log_date: logDate
      }

      const _savedMeal = await nutritionAPI.saveFoodPhotoAnalysis(saveData)

      // Invalidate queries to refresh UI. setDayMeals({}) clears the LOCAL
      // cache of expanded Recent Day meals, which TanStack does not know about —
      // without it a freshly saved meal stays invisible until the day is
      // collapsed and reopened, which reads as "it did not store".
      setDayMeals({})
      queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
      queryClient.invalidateQueries({ queryKey: ['meals', logDate] })
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
      if (logDate !== selectedViewDate) {
        queryClient.invalidateQueries({ queryKey: ['meals', selectedViewDate] })
      }

      // Set view to the saved date so user sees their meal
      setSelectedViewDate(logDate)

      console.log(`[Nutrition] Auto-save complete for ${mealType}`)

      // Check if there are more photos in batch
      const hasMorePhotos = files.length > 0 && fileIndex < files.length - 1

      if (hasMorePhotos) {
        // Automatically process next photo without user interaction
        const nextIndex = fileIndex + 1
        setCurrentBatchIndex(nextIndex)

        // Set state for consistent loading overlay
        setAnalysisResult(null)
        setSavedMealData(null)
        setIsAnalyzing(true)

        console.log(`[Nutrition] Auto-processing next photo ${nextIndex + 1}/${files.length}`)

        // Small delay to allow React to render the loading state
        await new Promise(resolve => setTimeout(resolve, 50))

        const nextFile = files[nextIndex]
        const formData = new FormData()
        formData.append('file', nextFile)

        try {
          const response = await nutritionAPI.analyzeFoodPhoto(formData)
          // Recursively process - this will auto-save and continue to next
          await handleAnalysisComplete(response.data, files, nextIndex)
        } catch (error) {
          console.error('[Nutrition] Failed to analyze next batch photo:', error)
          setAutoSaveError(error.response?.data?.detail || 'Failed to analyze photo')
          setIsSaving(false)
        }
      } else {
        // Last photo in batch or single photo - close modal
        console.log(`[Nutrition] Batch complete, closing modal`)
        handleClosePhotoUpload()
      }

    } catch (error) {
      console.error('[Nutrition] Auto-save failed:', error)
      setAutoSaveError(error.response?.data?.detail || 'Auto-save failed')
      // Fall back to manual review mode
      setAnalysisResult(result)
      setShowMealTypeSelector(false)
    } finally {
      setIsSaving(false)
    }
  }

  const handleAcceptAnalysis = async (_analysis) => {
    // Reset saving flag now that user is reviewing
    setIsSaving(false)
    // Show meal type selector
    setShowMealTypeSelector(true)

    // VH-23: Fetch existing meals for the choice UI
    try {
      const mealsResponse = await nutritionAPI.getMealsByDate(selectedDate)
      const mealsOfType = (mealsResponse.data?.meals || []).filter(
        (m) => m.meal_type?.toLowerCase() === selectedMealType.toLowerCase()
      )
      setExistingMealsForPhoto(mealsOfType)
      setSelectedPhotoMealId(null)
    } catch {
      setExistingMealsForPhoto([])
    }
  }

  const handleSaveAnalysis = async () => {
    if (!analysisResult) return

    setIsSaving(true)
    try {
      // Prepare the data for saving - use noon as default time
      const mealDateTime = new Date(`${selectedDate}T12:00:00`)

      const saveData = {
        analysis_data: analysisResult,
        meal_type: selectedMealType,
        meal_time: mealDateTime.toISOString(),
        log_date: selectedDate,
        meal_id: selectedPhotoMealId
      }

      await nutritionAPI.saveFoodPhotoAnalysis(saveData)

      // Refetch food logs and meals for the saved date
      queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
      queryClient.invalidateQueries({ queryKey: ['meals', selectedDate] })
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
      // Also invalidate the currently viewed date if it's different
      if (selectedDate !== selectedViewDate) {
        queryClient.invalidateQueries({ queryKey: ['meals', selectedViewDate] })
      }

      // If batch processing and more photos, move to next
      if (batchFiles.length > 0 && currentBatchIndex < batchFiles.length - 1) {
        const nextIndex = currentBatchIndex + 1
        setCurrentBatchIndex(nextIndex)

        // Clear current analysis to show we're processing
        setAnalysisResult(null)
        setShowMealTypeSelector(false)

        // Trigger next file analysis directly
        const nextFile = batchFiles[nextIndex]

        // Give React a moment to render the loading screen before starting API call
        await new Promise(resolve => setTimeout(resolve, 100))

        const formData = new FormData()
        formData.append('file', nextFile)

        try {
          const response = await nutritionAPI.analyzeFoodPhoto(formData)

          // Add batch info to response
          response.data.batchInfo = {
            currentIndex: nextIndex + 1,
            totalFiles: batchFiles.length,
            hasMore: nextIndex < batchFiles.length - 1
          }

          // Show analysis result
          setAnalysisResult(response.data)
          // DON'T reset isSaving here - it will be reset when user clicks Accept
        } catch (error) {
          console.error('Error analyzing next photo:', error)
          alert(t('nutrition.errors.analyzeNextFailed', { error: error.response?.data?.detail || error.message || t('nutrition.errors.unknownError') }))
          // Reset to upload mode
          setShowPhotoUpload(false)
          setBatchFiles([])
          setCurrentBatchIndex(0)
          setPhotoPreviews([])
          setIsSaving(false)
        }
      } else {
        // Last photo or single photo - show success and close
        alert(t('nutrition.success.foodLogged'))
        setAnalysisResult(null)
        setShowMealTypeSelector(false)
        setShowPhotoUpload(false)
        setBatchFiles([])
        setCurrentBatchIndex(0)
        setPhotoPreviews([])
        setIsSaving(false)
      }
    } catch (error) {
      console.error('Error saving food analysis:', error)

      // Better error message handling
      let errorMessage = t('nutrition.errors.saveFoodAnalysisPrefix')
      if (error.response?.data?.detail) {
        // Handle array of validation errors
        if (Array.isArray(error.response.data.detail)) {
          errorMessage += error.response.data.detail.map(err =>
            `${err.loc?.join(' -> ') || 'Field'}: ${err.msg}`
          ).join(', ')
        } else if (typeof error.response.data.detail === 'object') {
          errorMessage += JSON.stringify(error.response.data.detail)
        } else {
          errorMessage += error.response.data.detail
        }
      } else if (error.message) {
        errorMessage += error.message
      } else {
        errorMessage += t('nutrition.errors.unknownError')
      }

      alert(errorMessage)
      setIsSaving(false)
    }
  }

  const handleNextPhoto = () => {
    // Clear analysis to show next photo upload
    setAnalysisResult(null)
    setShowMealTypeSelector(false)
    // Trigger next file processing
    if (uploadComponentRef.current?.handleNextFile) {
      uploadComponentRef.current.handleNextFile()
    }
  }

  const handleRejectAnalysis = () => {
    setIsSaving(false)
    setAnalysisResult(null)
    setShowMealTypeSelector(false)
    // Don't clear batch files - user might want to try again with same batch
  }

  // Re-analyze with user hint
  const handleReanalyzeWithHint = async (userHint) => {
    // Get current file from batch or use the stored file
    const currentFile = batchFiles.length > 0 ? batchFiles[currentBatchIndex] : null

    if (!currentFile) {
      console.error('[Nutrition] No file available for re-analysis')
      alert(t('nutrition.errors.noFileForReanalysis', 'No file available for re-analysis'))
      return
    }

    setIsSaving(true)
    setAnalysisResult(null)

    try {
      const formData = new FormData()
      formData.append('file', currentFile)

      console.log(`[Nutrition] Re-analyzing with hint: "${userHint}"`)
      const response = await nutritionAPI.analyzeFoodPhoto(formData, userHint)

      // Add batch info to result
      const resultWithBatch = {
        ...response.data,
        batchInfo: batchFiles.length > 1 ? {
          currentIndex: currentBatchIndex + 1,
          totalFiles: batchFiles.length,
          hasMore: currentBatchIndex < batchFiles.length - 1
        } : null
      }

      setAnalysisResult(resultWithBatch)
      console.log('[Nutrition] Re-analysis complete with hint')
    } catch (error) {
      console.error('[Nutrition] Re-analysis with hint failed:', error)
      alert(error.response?.data?.detail || t('nutrition.errors.reanalysisFailed', 'Re-analysis failed. Please try again.'))
    } finally {
      setIsSaving(false)
    }
  }

  // VH-173: analysis failed. The overlay is driven by `isAnalyzing`, so it MUST
  // be cleared here or the spinner runs forever with the upload component
  // hidden behind it. The error is shown inline rather than in an alert(),
  // which says nothing about state and cannot be recovered from.
  const handleAnalysisError = (error) => {
    setIsAnalyzing(false)
    setIsSaving(false)
    // VH-238: an empty credit balance is not a failed analysis. Retrying cannot help, and the raw
    // code would be rendered as-is — which is how "AI analysis failed" reached me as a bug report.
    setAutoSaveError(
      isOutOfCredit(error)
        ? t('nutrition.errors.aiOutOfCredit')
        : apiErrorMessage(error, t('nutrition.errors.photoAnalysisFailed'))
    )
  }

  const handleClosePhotoUpload = () => {
    setPhotoSourceMode(null)
    setShowPhotoUpload(false)
    setAnalysisResult(null)
    setSavedMealData(null)
    setAutoSaveError(null)
    setIsAnalyzing(false)
    setIsSaving(false)
    setBatchFiles([])
    setCurrentBatchIndex(0)
    setPhotoPreviews([])
    setExistingMealsForPhoto([])
    setSelectedPhotoMealId(null)
  }

  // Process next photo in batch after auto-save
  const handleProcessNextBatchPhoto = async () => {
    const nextIndex = currentBatchIndex + 1
    const hasMorePhotos = nextIndex < batchFiles.length

    if (hasMorePhotos) {
      setCurrentBatchIndex(nextIndex)
      setSavedMealData(null)
      setAnalysisResult(null)

      // The FoodPhotoUpload will auto-trigger analysis when file is processed
      const nextFile = batchFiles[nextIndex]
      const formData = new FormData()
      formData.append('file', nextFile)

      setIsSaving(true)
      try {
        const response = await nutritionAPI.analyzeFoodPhoto(formData)
        await handleAnalysisComplete(response.data, batchFiles, nextIndex)
      } catch (error) {
        console.error('[Nutrition] Failed to analyze next batch photo:', error)
        setAutoSaveError(error.response?.data?.detail || 'Failed to analyze photo')
        setIsSaving(false)
      }
    } else {
      // Last photo - close the modal
      handleClosePhotoUpload()
    }
  }

  // Dismiss saved meal card and close modal
  const handleDismissSavedMeal = () => {
    setSavedMealData(null)
    setAnalysisResult(null)
    handleClosePhotoUpload()
  }

  const handleDeleteMeal = async (mealId) => {
    console.log('handleDeleteMeal called with mealId:', mealId)
    try {
      console.log('Calling deleteMealMutation.mutateAsync...')
      await deleteMealMutation.mutateAsync(mealId)
      console.log('Delete successful')
      alert(t('nutrition.success.mealDeleted'))
    } catch (error) {
      console.error('Delete error:', error)
      // Error already handled by mutation onError
    }
  }

  return (
    <div className="p-6">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('nutrition.title')}</h1>
          <p className="mt-2 text-gray-600">
            {t('nutrition.subtitle')}
          </p>
        </div>
        {/* VH-165: the desktop header row. Hidden on phones, where
            MealLogActions below is the single place to log. */}
        <div className="hidden lg:flex space-x-3">
          <button
            onClick={() => { setPhotoSourceMode(null); setShowPhotoUpload(true) }}
            className="btn-primary"
          >
            <CameraIcon className="h-5 w-5 mr-2" />
            {t('nutrition.logWithPhoto')}
          </button>
          <button
            onClick={() => setShowManualTextEntry(true)}
            className="btn-outline"
          >
            <PencilSquareIcon className="h-5 w-5 mr-2" />
            {t('nutrition.manualEntry.title')}
          </button>
        </div>
      </div>

      {/* VH-165: the phone block — the ONE place to log a meal on a phone.
          Hidden from lg up, where the header row above does the same job.
          Split by breakpoint rather than a JS viewport check: the codebase has
          no media-query hook, and CSS cannot disagree with itself at first
          paint. Both trees mount, but every handler below comes from this
          page, so they are two presentations of one state, never two copies. */}
      <div className="lg:hidden mb-6">
        <MealLogActions
          onTakePhoto={() => { setPhotoSourceMode('camera'); setShowPhotoUpload(true) }}
          onChooseFromLibrary={() => { setPhotoSourceMode('file'); setShowPhotoUpload(true) }}
          onManualEntry={() => setShowManualTextEntry(true)}
          onFavourites={() => setShowFavouritesSheet(true)}
        />
      </div>

      {/* Photo Upload Modal */}
      {showPhotoUpload && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-container rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface-container border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {analysisResult ? t('nutrition.modal.reviewAnalysis') : t('nutrition.modal.logWithPhoto')}
                </h2>
                {batchFiles.length > 1 && (
                  <p className="text-sm text-gray-600 mt-1">
                    {t('nutrition.modal.processingPhotos', { count: batchFiles.length })}
                  </p>
                )}
              </div>
              <button
                onClick={handleClosePhotoUpload}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 min-h-[500px]">
              {/* Loading state while analyzing or saving - consistent for all photos */}
              {/* Analysing/saving feedback. NOT gated on batchFiles: the upload
                  component is hidden the moment isAnalyzing flips, so if the
                  batch state was not populated yet BOTH branches rendered
                  nothing and the user stared at an empty modal for the ~12s the
                  analysis takes. Reported from a phone as "just a purple screen
                  is coming". Any in-flight state must show something. */}
              {(isAnalyzing || isSaving) && !analysisResult && (
                <div className="space-y-6 min-h-[400px]">
                  {/* Photo Preview */}
                  {photoPreviews[currentBatchIndex] && (
                    <div className="relative min-h-[400px]">
                      <img
                        src={photoPreviews[currentBatchIndex]}
                        alt="Food being analyzed"
                        className="w-full h-96 object-contain rounded-lg border-2 border-gray-200"
                      />
                      {/* Semi-transparent overlay at bottom - photo stays visible */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent rounded-b-lg p-6">
                        <div className="flex items-center justify-center space-x-4">
                          <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <div>
                            <p className="text-white font-medium">{t('nutrition.modal.analyzingPhoto')}</p>
                            <p className="text-sm text-gray-200">
                              {t('nutrition.modal.photoProgress', { current: currentBatchIndex + 1, total: batchFiles.length })}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Fallback if preview not loaded yet */}
                  {!photoPreviews[currentBatchIndex] && (
                    <div className="flex flex-col items-center justify-center p-12 min-h-[400px] border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                      <svg className="animate-spin h-12 w-12 text-indigo-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <p className="text-gray-900 font-medium mb-2">
                        {isSaving
                          ? t('nutrition.modal.savingMeal', { defaultValue: 'Saving meal...' })
                          : t('nutrition.modal.analyzingPhoto')}
                      </p>
                      {batchFiles.length > 0 && (
                        <p className="text-sm text-gray-600">
                          {t('nutrition.modal.photoProgress', { current: currentBatchIndex + 1, total: batchFiles.length })}
                        </p>
                      )}
                      <p className="text-sm text-gray-600 mt-1">
                        {t('nutrition.modal.analysisTakesAMoment', { defaultValue: 'This can take up to a minute.' })}
                      </p>
                      {/* VH-173: an explicit way out. The header X exists, but
                          while the overlay is up it is the only escape and is
                          easy to miss on a phone — reported as "no way to
                          interrupt". A wait the user cannot leave is a trap. */}
                      <button
                        type="button"
                        onClick={handleClosePhotoUpload}
                        className="mt-4 btn-outline"
                        data-testid="cancel-analysis"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Keep both components mounted to preserve state */}
              <div style={{ display: (analysisResult || isAnalyzing || isSaving) ? 'none' : 'block' }}>
                <FoodPhotoUpload
                  ref={uploadComponentRef}
                  autoStartMode={photoSourceMode}
                  onAnalysisError={handleAnalysisError}
                  onAnalysisStart={handleAnalysisStart}
                  onAnalysisComplete={handleAnalysisComplete}
                  onCancel={handleClosePhotoUpload}
                />
              </div>

              {/* Show SavedMealEditCard after successful auto-save */}
              {savedMealData && analysisResult && (
                <SavedMealEditCard
                  savedMeal={savedMealData}
                  analysisResult={analysisResult}
                  onDismiss={handleDismissSavedMeal}
                  onProcessNext={handleProcessNextBatchPhoto}
                  batchInfo={batchFiles.length > 1 ? {
                    currentIndex: currentBatchIndex + 1,
                    totalFiles: batchFiles.length,
                    hasMore: currentBatchIndex < batchFiles.length - 1
                  } : null}
                />
              )}

              {/* Show FoodAnalysisReview only if auto-save failed (fallback to manual mode) */}
              {analysisResult && !savedMealData && !showMealTypeSelector && autoSaveError && (
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800 font-medium">{t('nutrition.autoSave.failed', 'Auto-save failed. Please review and save manually.')}</p>
                    <p className="text-sm text-yellow-600 mt-1">{autoSaveError}</p>
                  </div>
                  <FoodAnalysisReview
                    analysis={analysisResult}
                    onAccept={handleAcceptAnalysis}
                    onReject={handleRejectAnalysis}
                    onReanalyzeWithHint={handleReanalyzeWithHint}
                    onNextPhoto={handleNextPhoto}
                  />
                </div>
              )}

              {/* Meal Type and Time Selector */}
              {analysisResult && showMealTypeSelector && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-900 font-medium">{t('nutrition.modal.selectMealTime')}</p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('nutrition.mealType')}
                      </label>
                      <select
                        value={selectedMealType}
                        onChange={(e) => setSelectedMealType(e.target.value)}
                        className="form-input w-full"
                      >
                        <option value="breakfast">{t('nutrition.mealTypes.breakfast')}</option>
                        <option value="lunch">{t('nutrition.mealTypes.lunch')}</option>
                        <option value="dinner">{t('nutrition.mealTypes.dinner')}</option>
                        <option value="snack">{t('nutrition.mealTypes.snack')}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('common.date')}
                      </label>
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className="form-input w-full"
                      />
                    </div>

                    {/* VH-23: Meal target selection when existing meals found */}
                    {existingMealsForPhoto.length > 0 ? (
                      <MealTargetSelector
                        existingMeals={existingMealsForPhoto}
                        selectedMealId={selectedPhotoMealId}
                        onSelect={setSelectedPhotoMealId}
                      />
                    ) : (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-900">
                          <strong>{t('nutrition.modal.note')}:</strong> {t('nutrition.modal.autoGrouping')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Summary Display */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">{t('nutrition.mealSummary')}</h3>
                    <div className="grid grid-cols-4 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-indigo-600">
                          {analysisResult.meal_total?.calories?.toFixed(0) || 0}
                        </div>
                        <div className="text-xs text-gray-600">{t('nutrition.calories')}</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-blue-600">
                          {analysisResult.meal_total?.protein_g?.toFixed(1) || 0}g
                        </div>
                        <div className="text-xs text-gray-600">{t('nutrition.protein')}</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-600">
                          {analysisResult.meal_total?.carbohydrates_g?.toFixed(1) || 0}g
                        </div>
                        <div className="text-xs text-gray-600">{t('nutrition.carbs')}</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-amber-600">
                          {analysisResult.meal_total?.fat_g?.toFixed(1) || 0}g
                        </div>
                        <div className="text-xs text-gray-600">{t('nutrition.fat')}</div>
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-gray-600">
                      {t('nutrition.foodItemsIdentified', { count: analysisResult.foods_identified?.length || 0 })}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-4">
                    <button
                      onClick={handleSaveAnalysis}
                      disabled={isSaving}
                      className="flex-1 btn-primary"
                    >
                      {isSaving ? t('common.saving') : t('nutrition.saveMeal')}
                    </button>
                    <button
                      onClick={() => setShowMealTypeSelector(false)}
                      disabled={isSaving}
                      className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      {t('common.back')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-sm text-gray-600">{t('nutrition.loadingData')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Date Selector */}
            <div className="bg-surface-container rounded-lg shadow-md border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">{t('nutrition.viewMealsFor')}:</label>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      const prevDate = new Date(selectedViewDate)
                      prevDate.setDate(prevDate.getDate() - 1)
                      setSelectedViewDate(getLocalDateString(prevDate))
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Previous day"
                  >
                    <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <input
                    type="date"
                    value={selectedViewDate}
                    onChange={(e) => setSelectedViewDate(e.target.value)}
                    max={today}
                    className="form-input"
                  />
                  <button
                    onClick={() => {
                      const nextDate = new Date(selectedViewDate)
                      nextDate.setDate(nextDate.getDate() + 1)
                      const nextDateStr = getLocalDateString(nextDate)
                      if (nextDateStr <= today) {
                        setSelectedViewDate(nextDateStr)
                      }
                    }}
                    disabled={selectedViewDate >= today}
                    className={`p-2 rounded-lg transition-colors ${
                      selectedViewDate >= today
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'hover:bg-gray-100 text-gray-600'
                    }`}
                    title="Next day"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  {selectedViewDate !== today && (
                    <button
                      onClick={() => setSelectedViewDate(today)}
                      className="ml-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium px-3 py-1 rounded-lg hover:bg-indigo-50"
                    >
                      {t('time.today')}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Summary Card for Selected Date */}
            <TodaySummaryCard
              calories={selectedDateLog?.total_calories || 0}
              protein={selectedDateLog?.total_protein_g || selectedDateLog?.total_protein || 0}
              carbs={selectedDateLog?.total_carbohydrates_g || selectedDateLog?.total_carbs || 0}
              fat={selectedDateLog?.total_fat_g || selectedDateLog?.total_fat || 0}
              goals={userGoals}
              mealCount={selectedDateLog?.meal_count || 0}
              date={new Date(selectedViewDate)}
            />

            {/* VH-229 refinement: the one weekly plan — week totals vs targets, link to the plan, one-click logging */}
            <WeekPlanCard
              date={selectedViewDate}
              onLogged={() => {
                queryClient.invalidateQueries({ queryKey: ['meals', selectedViewDate] })
                queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
                queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
                queryClient.invalidateQueries({ queryKey: ['healthCycleWeeks'] })
              }}
            />

            {/* Meals Timeline for Selected Date */}
            <div className="bg-surface-container rounded-lg shadow-md border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {selectedViewDate === today ? t('nutrition.todaysMeals') : t('nutrition.mealsFor', { date: new Date(selectedViewDate).toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }) })}
              </h2>

              {isMealsLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  <p className="mt-2 text-sm text-gray-600">{t('nutrition.loadingMeals')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Organize meals by type and show all meals */}
                  {(() => {
                    // Create a map of meal types to arrays of meals, sorted by time within each type
                    const mealsByType = {}
                    selectedDateMeals?.meals?.forEach(meal => {
                      const type = (meal.meal_type || '').toUpperCase()
                      if (!mealsByType[type]) {
                        mealsByType[type] = []
                      }
                      mealsByType[type].push(meal)
                    })
                    // Sort meals within each type by meal_time
                    Object.values(mealsByType).forEach(meals => {
                      meals.sort((a, b) => new Date(a.meal_time) - new Date(b.meal_time))
                    })

                    const mealTypes = ['BREAKFAST', 'LUNCH', 'DINNER', 'SNACK', 'OTHER']

                    return mealTypes.map(mealType => {
                      const meals = mealsByType[mealType] || []

                      if (meals.length > 0) {
                        // Show all meals of this type
                        return (
                          <div key={mealType} className="space-y-2">
                            {meals.map(meal => (
                              <MealTimelineCard
                                key={meal.id}
                                mealId={meal.id}
                                mealType={meal.meal_type}
                                mealTime={meal.meal_time}
                                logDate={meal.log_date}
                                photo={meal.photo_url}
                                note={meal.description}
                                foods={meal.meal_foods || []}
                                totals={{
                                  calories: meal.total_calories || 0,
                                  protein: meal.total_protein_g || 0,
                                  carbs: meal.total_carbohydrates_g || 0,
                                  fat: meal.total_fat_g || 0
                                }}
                                isExpanded={expandedMeals[meal.id] || false}
                                onToggle={() => setExpandedMeals(prev => ({
                                  ...prev,
                                  [meal.id]: !prev[meal.id]
                                }))}
                                onEdit={() => {}}
                                onDelete={() => handleDeleteMeal(meal.id)}
                                onCopyMeal={handleCopyMealToDate}
                                onAddPhoto={() => {}}
                                onMealUpdated={() => {
                                  setDayMeals({}) // Clear stale dayMeals cache
                                  queryClient.invalidateQueries({ queryKey: ['meals'] })
                                  queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
                                  queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
                                }}
                              />
                            ))}
                          </div>
                        )
                      } else {
                        // Show placeholder only for current/future dates
                        const isCurrentOrFutureDate = selectedViewDate >= today
                        if (!isCurrentOrFutureDate) {
                          return null // Don't show placeholders for past dates with no meals
                        }

                        return (
                          <MealPlaceholder
                            key={mealType}
                            mealType={mealType}
                            copySource={copySources[mealType]}
                            onCopyMeal={handleCopyMeal}
                            isCopying={copyingMealType === mealType}
                            onLogMeal={(type) => {
                              setSelectedMealType(type.toLowerCase())
                              setSelectedDate(selectedViewDate) // Set the date for the photo upload
                              setShowPhotoUpload(true)
                            }}
                          />
                        )
                      }
                    })
                  })()}

                  {/* Empty state for past dates with no meals */}
                  {selectedViewDate < today && (!selectedDateMeals?.meals || selectedDateMeals.meals.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      <p>{t('nutrition.noMealsForDate')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Recent Days Section */}
            {(() => {
              // Defend against the API serializing either field as a non-array
              // truthy value (was the latent prod crash trigger 2026-06-04: a
              // string would silently pass `.slice().map()` and crash at `.map`).
              const recentLogs = toArrayOr(foodLogs?.food_logs, toArrayOr(foodLogs?.data))
              if (recentLogs.length === 0) return null
              return (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900">{t('nutrition.recentDays')}</h2>
                {recentLogs.slice(0, 7).map((log, _index) => {
                  // Skip the currently selected date since it's shown in the section above
                  const logDate = log.log_date || log.date
                  const logDateOnly = logDate.split('T')[0]
                  if (logDateOnly === selectedViewDate) {
                    return null
                  }

                  return (
                    <RecentDayCard
                      key={log.id}
                      date={logDate}
                      totals={{
                        calories: log.total_calories || 0,
                        protein: log.total_protein_g || log.total_protein || 0,
                        carbs: log.total_carbohydrates_g || log.total_carbs || 0,
                        fat: log.total_fat_g || log.total_fat || 0
                      }}
                      mealCount={log.meal_count || 0}
                      meals={dayMeals[logDate] || []}
                      goals={userGoals}
                      isExpanded={expandedDays[log.id] || false}
                      onToggle={() => handleDayToggle(log.id, logDate)}
                      onDeleteMeal={handleDeleteMeal}
                      onCopyMeal={handleCopyMealToDate}
                      onMealUpdated={() => {
                        // Clear stale dayMeals cache so it refetches on next expand
                        setDayMeals(prev => {
                          const updated = { ...prev }
                          delete updated[logDate]
                          return updated
                        })
                        queryClient.invalidateQueries({ queryKey: ['meals'] })
                        queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
                        queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
                      }}
                    />
                  )
                })}
              </div>
              )
            })()}

            {/* Empty state */}
            {(() => {
              const recentLogs = toArrayOr(foodLogs?.food_logs, toArrayOr(foodLogs?.data))
              if (recentLogs.length > 0) return null
              return (
              <div className="text-center py-12 bg-surface-container rounded-lg shadow-md border border-gray-200">
                <CakeIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('nutrition.noFoodLogs')}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t('nutrition.noFoodLogsHint')}
                </p>
                <button
                  onClick={() => setShowPhotoUpload(true)}
                  className="mt-4 btn-primary inline-flex items-center"
                >
                  <CameraIcon className="h-5 w-5 mr-2" />
                  {t('nutrition.logFirstMeal')}
                </button>
              </div>
              )
            })()}
          </div>

          {/* Sidebar - Right column.
              VH-165: hidden on phones. This column stacks BELOW the whole day's
              meals and Recent Days there, which is how Favourites ended up far
              from the other two logging actions. On a phone it is reached from
              MealLogActions instead. */}
          <div className="hidden lg:block lg:col-span-1 space-y-6">
            {/* Favorite Meals Widget */}
            <FavoriteMealsWidget
              selectedDate={selectedViewDate}
              onMealAdded={() => {
                queryClient.invalidateQueries({ queryKey: ['meals', selectedViewDate] })
                queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
                queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
              }}
              onManageFavorites={() => setShowFavoritesModal(true)}
            />

            <WeeklySummaryWidget
              weekStart={weeklySummary?.period?.start_date ? new Date(weeklySummary.period.start_date) : new Date(new Date().setDate(new Date().getDate() - 7))}
              weekEnd={weeklySummary?.period?.end_date ? new Date(weeklySummary.period.end_date) : new Date()}
              avgDailyCalories={weeklySummary?.weekly_averages?.daily_calories || 0}
              totalWeeklyCalories={weeklySummary?.weekly_totals?.total_calories || 0}
              totalWeeklyProtein={weeklySummary?.weekly_totals?.total_protein_g || 0}
              totalWeeklyCarbs={weeklySummary?.weekly_totals?.total_carbs_g || 0}
              totalWeeklyFat={weeklySummary?.weekly_totals?.total_fat_g || 0}
              goalAdherence={weeklySummary?.daily_breakdown?.length > 0
                ? Math.round(weeklySummary.daily_breakdown.reduce((sum, day) => sum + (day.goal_adherence_pct || 0), 0) / weeklySummary.daily_breakdown.length)
                : 0}
              mealsLogged={weeklySummary?.daily_breakdown?.reduce((sum, day) => sum + (day.meal_count || 0), 0) || 0}
              totalMealsExpected={21}
              topFoods={toArrayOr(weeklySummary?.meal_insights?.top_foods_by_frequency).slice(0, 5).map(f => ({
                name: f.name,
                frequency: f.count
              }))}
            />
          </div>
        </div>
      )}

      {/* Manual Text Entry Modal */}
      {showManualTextEntry && (
        <ManualFoodTextEntry
          onClose={() => setShowManualTextEntry(false)}
          selectedMealType={selectedMealType}
          selectedDate={selectedDate}
          onSuccess={() => {
            // Refetch data after successful save
            queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
            queryClient.invalidateQueries({ queryKey: ['meals'] })
            queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
          }}
        />
      )}

      {/* Favorites Management Modal */}
      <FavoritesQuickPickSheet
        open={showFavouritesSheet}
        onClose={() => setShowFavouritesSheet(false)}
        selectedDate={selectedViewDate}
        onManage={() => setShowFavoritesModal(true)}
        onMealAdded={() => {
          setDayMeals({})
          queryClient.invalidateQueries({ queryKey: ['meals'] })
          queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
          queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
        }}
      />

      <FavoriteMealsModal
        isOpen={showFavoritesModal}
        onClose={() => setShowFavoritesModal(false)}
        selectedDate={selectedViewDate}
        onMealAdded={() => {
          queryClient.invalidateQueries({ queryKey: ['meals', selectedViewDate] })
          queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
          queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
        }}
      />
    </div>
  )
}